import { useParams, useLocation, useLoaderData } from 'react-router-dom'
import { useState, useEffect } from 'react'
import sanityClient, { previewClient } from '../client.js'
import { PortableText } from '@portabletext/react'
import { NavBar } from '../components/NavBar.jsx'
import imageUrlBuilder from '@sanity/image-url'
import { Infographic } from '../components/Infographic.jsx'
import { Footer } from '../components/Footer.jsx'
import debounce from 'lodash.debounce'
import { BondInfographic } from '../sections/special/BondInfographic.jsx'
import { StudentMusic } from '../sections/special/StudentMusic.jsx'
import { ShareButtons } from '../components/ShareButtons'
import { motion, AnimatePresence } from 'motion/react'
import { AudioProvider } from '../audio-components/AudioProvider'
import { AudioPlayer } from '../audio-components/player/AudioPlayer'

const builder = imageUrlBuilder(sanityClient)

function urlFor(source) {
  return builder.image(source)
}

export const Story = ({ year }) => {
  // Get the pre-loaded data
  const storyData = useLoaderData()
  const location = useLocation()

  // if there are two params, set the first to year and the second to slug
  const { slug } = useParams()
  const [preview, setPreview] = useState(false)
  const [showCard, setShowCard] = useState(false)
  const [postId, setPostId] = useState(null)

  let queryParams
  if (typeof window !== 'undefined') {
    queryParams = new URLSearchParams(window.location.search)
  }

  const idQuery = /* js */ `*[slug.current == "${slug}"]{
    _id,
  }`

  const draftQuery = /* js */ `
  *[slug.current == "${slug}"]{
    title,
    slug,
    mainImage {
      ..., // this will ensure you keep the existing data
      ...asset-> {
        altText,
        caption,
        ...metadata {
          lqip, // the lqip can be used for blurHashUrl or other low-quality placeholders
          ...dimensions {
            width,
            height
          }
        }
      }
    },
    "mainImageHotSpot": mainImage.hotspot,
    imageColorStart,
    imageColorEnd,
    firstByline,
    secondByline,
    body,
    infographicOverline,
    infographicTitle,
    iconColor,
    citations,
        video,
    featuredVideo,
    videoTitle,
    videoDescription,
    videoOverline,
    video,
    seoDescription,
    seoImage,
    timeToRead,
    cardImage,
    infographics[]{
      title,
      description,
      icon,
    },
  }`

  const query = /* js */ `
  *[slug.current == "${slug}"]{
    title,
    slug,
    mainImage {
      ..., // this will ensure you keep the existing data
      ...asset-> {
        altText,
        caption,
        ...metadata {
          lqip, // the lqip can be used for blurHashUrl or other low-quality placeholders
          ...dimensions {
            width,
            height
          }
        }
      }
    },
    "mainImageHotSpot": mainImage.hotspot,
    imageColorStart,
    imageColorEnd,
    firstByline,
    secondByline,
    body,
    infographicOverline,
    infographicTitle,
    iconColor,
    citations,
    video,
    featuredVideo,
    videoTitle,
    videoDescription,
    videoOverline,
    video,
    seoDescription,
    seoImage,
    timeToRead,
    cardImage,
    infographics[]{
      title,
      description,
      icon,
    },
  }`

  // Use the pre-loaded data or fetch if not available
  const [story, setStory] = useState(storyData || null)

  useEffect(() => {
    // Check if the URL contains an anchor
    if (!window.location.hash) {
      // If no anchor, scroll to the top
      window.scrollTo(0, 0)
    }

    // Only fetch if we don't have the data
    if (!storyData) {
      sanityClient.fetch(query).then((data) => {
        setStory(data[0])
      })
    }
  }, [storyData, query])

  function getSchool() {
    sanityClient
      .fetch(query)
      .then((data) => {
        setStory(data[0])
      })
      .catch(console.error)
  }

  function getSchoolPreview() {
    previewClient
      .fetch(query)
      .then((data) => {
        setStory(data[0])
      })
      .catch(console.error)
    previewClient.listen(query).subscribe((data) => {
      setStory(data.result)
    })
  }

  // debounce getSchoolDraftPreview to prevent multiple requests
  const getSchoolDraftPreview = debounce(() => {
    previewClient
      .fetch(draftQuery)
      .then((data) => {
        setStory(data[0])
      })
      .catch(console.error)
  }, 1000)

  function watchSchool() {
    previewClient.fetch(idQuery).then((data) => {
      setPostId(data[0]._id)
    })
  }

  function listenDraft() {
    previewClient.listen(draftQuery).subscribe((data) => {
      getSchoolDraftPreview()
    })
  }

  function checkForDraft(postId) {
    sanityClient
      .fetch(
        /* js */ `
          *[_id == "drafts.${postId}"]{
            _id,
          }`
      )
      .then((draft) => {
        if (draft.length > 0) {
          getSchoolDraftPreview()
          listenDraft()
        } else {
          getSchoolPreview()
        }
      })
  }

  useEffect(() => {
    if (!!postId) {
      checkForDraft(postId)
    }
  }, [postId])

  useEffect(() => {
    setPreview(queryParams.get('livepreview'))
    if (queryParams.get('livepreview')) {
      watchSchool()
    } else {
      getSchool()
    }
  }, [location])

  // bg-[#862633]
  // bg-[#003b5c]
  // bg-[#77c5d5]
  // bg-[#ffc845]
  // bg-[#8f993e]
  // bg-[#ff7f41]

  // from-[#862633]
  // from-[#003b5c]
  // from-[#77c5d5]
  // from-[#ffc845]
  // from-[#8f993e]
  // from-[#ff7f41]

  // to-[#862633]
  // to-[#003b5c]
  // to-[#77c5d5]
  // to-[#ffc845]
  // to-[#8f993e]
  // to-[#ff7f41]
  const [isLoaded, setIsLoaded] = useState(false)
  const [showNotification, setShowNotification] = useState(false)

  return (
    <>
      <AudioProvider>
        <div>
          <NavBar year={year} />

          {story?.featuredVideo ? (
            <>
              {story?.video && (
                <div class="relative inset-0 flex w-full flex-col items-center justify-center overflow-hidden bg-black bg-gradient-to-tr from-neutral-900 to-sv-navy/70 py-12 shadow-inner blur-0 lg:py-24 lg:pb-32">
                  <img
                    className={
                      slug +
                      ` absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-overlay grayscale filter`
                    }
                    src={urlFor(story.mainImage).width(1920).url()}
                    alt={story?.mainImage?.alt}
                  />
                  <div class="relative w-full max-w-7xl px-8 text-white md:pb-48">
                    <div
                      class="overflow-hidden rounded-2xl transition-all duration-500 hover:shadow-2xl"
                      style={{
                        padding: '56.25% 0px 0px',
                        position: 'relative',
                      }}
                    >
                      {story?.video && story?.video?.includes('youtube') && (
                        <iframe
                          src={story?.video
                            ?.replace('watch?v=', 'embed/')
                            .replace('youtu.be/', 'youtube.com/embed/')}
                          frameborder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          style={{
                            position: 'absolute',
                            top: '0px',
                            left: '0px',
                            width: '100%',
                            height: '100%',
                          }}
                        />
                      )}
                      {story?.video && story?.video?.includes('vimeo') && (
                        <iframe
                          src={story?.video?.replace(
                            'vimeo.com/',
                            'player.vimeo.com/video/'
                          )}
                          frameborder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          style={{
                            position: 'absolute',
                            top: '0px',
                            left: '0px',
                            width: '100%',
                            height: '100%',
                          }}
                        />
                      )}
                    </div>
                    <h3 className="mt-8 font-condensed text-lg font-light uppercase text-sv-yellow lg:text-2xl">
                      {story?.videoOverline}
                    </h3>
                    <h4 className="mb-8 font-display text-3xl font-bold uppercase lg:text-5xl">
                      {story?.videoTitle}
                    </h4>
                    <p className="max-w-3xl font-body leading-loose lg:text-lg">
                      {story?.videoDescription}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div class="w-full blur-0">
              <div
                class={`relative mx-auto h-64 w-screen max-w-[1536px] overflow-hidden bg-gradient-to-tr before:absolute before:inset-y-0 before:left-0 before:z-10 before:w-[200px] before:bg-gradient-to-r before:from-white before:via-white/70 before:to-transparent before:opacity-0 before:transition-all lg:h-96`}
              >
                {story?.mainImage && (
                  <>
                    <img
                      src={urlFor(story.mainImage).width(1920).url()}
                      className={`${slug} absolute inset-0 h-full w-full object-cover  sm:![view-transition-name:unset]`}
                      alt={story?.mainImage?.alt}
                      style={{
                        objectPosition: `${story?.mainImageHotSpot?.x * 100}% ${story?.mainImageHotSpot?.y * 100}%`,
                        viewTransitionName:
                          story?.slug.current.toString() + '-image',
                      }}
                    />
                  </>
                )}
                <div class="relative mx-auto max-w-7xl md:px-8">
                  <div class="relative w-full"></div>
                </div>
              </div>
            </div>
          )}

          <div class="relative mx-auto w-full max-w-7xl bg-white blur-0 md:px-8 md:pt-16 lg:pb-32">
            <div class="relative grid w-full grid-cols-1 gap-8 lg:grid-cols-[auto,auto,400px]  xl:grid-cols-[auto,auto,440px] xl:gap-16">
              <div className="relative order-2 px-8 md:px-0 lg:order-none lg:mt-24">
                <div className="sticky top-[8vh] mx-auto w-full max-w-prose lg:pt-24">
                  <ShareButtons
                    title={story?.title}
                    description={story?.seoDescription}
                    showNotification={showNotification}
                    setShowNotification={setShowNotification}
                  />
                </div>
              </div>
              <div class="relative order-1 flex rounded-2xl p-8 pb-0 lg:order-none lg:p-0">
                <div class="relative mx-auto inline-block w-full sm:w-auto">
                  <div class="relative mx-auto h-auto w-full max-w-prose rounded-md lg:mx-0 lg:w-auto lg:max-w-none">
                    <div class="flex flex-col-reverse items-start md:flex-row md:items-center">
                      <div class="flex flex-col">
                        <h1 class="m-0 p-0">
                          <span
                            className={`font-condensed text-2xl font-bold uppercase leading-tight text-neutral-700 sm:font-display sm:text-4xl sm:normal-case sm:text-neutral-900 sm:![view-transition-name:unset]`}
                            style={{
                              viewTransitionName:
                                story?.slug.current.toString() + '-title',
                            }}
                          >
                            {story?.title}
                          </span>
                        </h1>
                        <div class="mt-6 mb-2 w-16 border-t-4 border-sv-green"></div>
                        {story?.firstByline && (
                          <p class="m-0 p-0 font-condensed font-bold uppercase text-sv-crimson">
                            By {story?.firstByline}
                          </p>
                        )}
                        {story?.secondByline && (
                          <p class="m-0 p-0 font-condensed uppercase text-sv-crimson">
                            {story?.secondByline}
                          </p>
                        )}
                      </div>
                    </div>
                    <div class="prose mt-8 max-w-prose prose-headings:font-display prose-headings:text-sv-crimson lg:max-w-none lg:prose-lg">
                      <PortableText
                        value={story?.body}
                        components={
                          // render quote block
                          {
                            types: {
                              quote: ({ children, value }) => (
                                <blockquote
                                  cite="Benjamin Franklin"
                                  className="my-6 border-sv-navy py-2 pl-2 text-2xl font-light leading-relaxed"
                                >
                                  "{value?.quote}"
                                  <span className="mt-3 block text-lg font-bold uppercase not-italic text-sv-crimson">
                                    {value?.originator}
                                  </span>
                                </blockquote>
                              ),
                              video: ({ children, value }) => (
                                <div className="relative flex items-center bg-neutral-100">
                                  <div
                                    className={`w-full overflow-hidden ${
                                      value?.vertical
                                        ? 'aspect-[9/16] min-[400px]:aspect-square'
                                        : 'aspect-video'
                                    }`}
                                  >
                                    {value?.url &&
                                      value?.url.includes('youtube') && (
                                        <iframe
                                          src={value?.url
                                            .replace('watch?v=', 'embed/')
                                            .replace(
                                              'youtu.be/',
                                              'youtube.com/embed/'
                                            )}
                                          frameBorder="0"
                                          allow="autoplay; fullscreen; picture-in-picture"
                                          allowFullScreen
                                          style={{
                                            position: 'absolute',
                                            top: '0px',
                                            left: '0px',
                                            width: '100%',
                                            height: '100%',
                                          }}
                                        />
                                      )}
                                    {value?.url &&
                                      value?.url.includes('vimeo') && (
                                        <iframe
                                          src={value?.url.replace(
                                            'vimeo.com/',
                                            'player.vimeo.com/video/'
                                          )}
                                          frameBorder="0"
                                          allow="autoplay; fullscreen; picture-in-picture"
                                          allowFullScreen
                                          style={{
                                            position: 'absolute',
                                            top: '0px',
                                            left: '0px',
                                            width: '100%',
                                            height: '100%',
                                          }}
                                        />
                                      )}
                                  </div>
                                </div>
                              ),
                              bondInfographic: ({ children, value }) => (
                                <div class="not-prose">
                                  <BondInfographic />
                                </div>
                              ),
                              studentMusic: ({ children, value }) => (
                                <div class="not-prose">
                                  <StudentMusic />
                                </div>
                              ),
                              image: ({ children, value }) => (
                                <div class="mb-8 flex flex-col items-center">
                                  {value && (
                                    <>
                                      <img
                                        src={urlFor(value)
                                          .width(1600)
                                          .fit('crop')
                                          .url()}
                                        className="max-h-[600px] object-cover"
                                      />
                                      <span class="mt-0 text-sm font-light text-neutral-500">
                                        {value?.caption}
                                      </span>
                                    </>
                                  )}
                                </div>
                              ),
                            },
                          }
                        }
                      />

                      {story?.citations && (
                        <p class="citations mt-12 border-t border-sv-green pt-4 text-sm font-light">
                          {story?.citations}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {story?.infographicTitle && story?.infographics?.length > 0 && (
                <motion.div
                  class="relative order-3 mx-auto mb-8 mt-12  px-8 max-md:!transform-none max-md:!transition-none md:px-0 lg:order-none lg:mt-24"
                  initial={{
                    opacity: 0,
                    transform: 'translateX(20px)',
                  }}
                  whileInView={{
                    opacity: 1,
                    transform: 'translateX(0px)',
                  }}
                  viewport={{
                    once: true,
                    amount: 0,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div class="sticky top-[8vh] mb-6 max-w-prose border border-neutral-200 px-8 pb-8 lg:max-h-[84vh] lg:overflow-y-auto">
                    <div class="sticky top-0 z-10 border-b border-b-neutral-200 bg-white px-4 pb-8 text-left font-body text-2xl font-bold uppercase text-neutral-900">
                      <p class="pt-8 font-display text-base font-light">
                        {story?.infographicOverline || 'By the Numbers'}
                      </p>
                      {story?.infographicTitle && (
                        <p class="font-sans text-2xl font-black">
                          {story?.infographicTitle}
                        </p>
                      )}
                    </div>
                    <div class="grid grid-cols-1 pt-4 sm:grid-cols-1 md:grid-cols-1 lg:max-h-full lg:grid-cols-1 ">
                      {story?.infographics?.map((infographic, index) => (
                        <Infographic
                          key={index}
                          title={infographic.title}
                          description={infographic.description}
                          icon={infographic.icon}
                          color={story?.iconColor?.value}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
        {/* Use story.video to render a video iframe from a youtube or vimeo link */}
        {!story?.featuredVideo && story?.video && (
          <div class="relative flex w-full flex-col items-center justify-center overflow-hidden  py-12  blur-0 lg:pt-0">
            <motion.div
              initial={{
                opacity: 0,
                filter: 'blur(20px)',
                transform: 'translateY(50px)',
              }}
              whileInView={{
                opacity: 1,
                filter: 'blur(0px)',
                transform: 'translateY(0px)',
              }}
              viewport={{
                once: true,
                amount: 0.5,
              }}
              transition={{ duration: 0.5 }}
              className="absolute inset-x-0 h-1/2 bg-gradient-to-r from-neutral-100 via-neutral-200 to-neutral-100"
            ></motion.div>
            <div class="relative w-full max-w-7xl px-8">
              <motion.div
                initial={{
                  opacity: 0,
                  filter: 'blur(20px)',
                  transform: 'translateY(50px)',
                }}
                whileInView={{
                  opacity: 1,
                  filter: 'blur(0px)',
                  transform: 'translateY(0px)',
                }}
                viewport={{
                  once: true,
                  amount: 0.1,
                }}
                transition={{ duration: 0.5 }}
                class="overflow-hidden border border-neutral-200 transition-all duration-500"
                style={{
                  padding: '56.25% 0px 0px',
                  position: 'relative',
                }}
              >
                {story?.video && story?.video?.includes('youtube') && (
                  <iframe
                    src={story?.video
                      ?.replace('watch?v=', 'embed/')
                      .replace('youtu.be/', 'youtube.com/embed/')}
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: '0px',
                      left: '0px',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                )}
                {story?.video && story?.video?.includes('vimeo') && (
                  <iframe
                    src={story?.video?.replace(
                      'vimeo.com/',
                      'player.vimeo.com/video/'
                    )}
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: '0px',
                      left: '0px',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                )}
              </motion.div>
            </div>
          </div>
        )}
        <Footer />

        {preview && (
          <div
            className={`fixed bottom-0 left-0 z-10 ml-2 mb-2 max-w-[26rem] transform transition-all duration-500 sm:max-w-[810px] ${
              showCard ? 'translate-y-0' : 'translate-y-[calc(100%-2.5rem)]'
            }`}
            onClick={() => setShowCard(!showCard)}
          >
            <div className="mb-2 inline-block min-w-[170px] cursor-pointer rounded-full bg-black px-4 py-2 text-center text-white hover:bg-neutral-800">
              {showCard ? 'Hide' : 'Show'} Card Preview
            </div>
            <div className="mt-1 flex flex-row gap-2">
              <div
                className={`group flex transform flex-col overflow-hidden rounded-3xl border-none shadow-sm outline-none transition-all duration-500 visited:opacity-50 hover:-translate-y-1 hover:rounded-none hover:opacity-100 hover:shadow-2xl focus:-translate-y-1 focus:rounded-none focus:shadow-2xl motion-reduce:transition-none`}
                style={{
                  filter: 'blur(0px)',
                }}
              >
                <div class="relative flex-shrink-0 overflow-hidden bg-black">
                  {story?.mainImage ? (
                    <img
                      class="h-72 w-full transform object-cover opacity-100 filter transition-all duration-500 group-hover:-translate-y-12 group-hover:scale-105 group-hover:grayscale-0 group-focus:-translate-y-12 group-focus:scale-105 group-focus:grayscale-0 md:h-96"
                      src={
                        story?.cardImage
                          ? urlFor(story?.cardImage)?.width(800).url()
                          : urlFor(story?.mainImage)?.width(800).url()
                      }
                      alt={story?.mainImage?.alt}
                    />
                  ) : (
                    <div class="h-72 w-full transform object-cover opacity-100 filter transition-all duration-500 group-hover:-translate-y-12 group-hover:scale-105 group-hover:grayscale-0 group-focus:-translate-y-12 group-focus:scale-105 group-focus:grayscale-0 md:h-96"></div>
                  )}
                  <div class="absolute left-0 right-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent opacity-70 transition-all duration-500 group-hover:h-full group-hover:opacity-100"></div>
                </div>
                <div class="pointer-events-none absolute bottom-0 w-full translate-y-40 transform transition-all duration-500 group-hover:translate-y-0 group-focus:translate-y-0">
                  <div class="flex flex-1 flex-col justify-between bg-transparent p-6">
                    <div class="flex-1">
                      <p class="text-sm font-medium text-white">
                        <span class="">Make a Difference.</span>
                      </p>
                      <div class="mt-2 block">
                        <p class="text-xl font-semibold text-white">
                          {story?.title}
                        </p>
                        <p class="mt-3 text-base text-neutral-100 line-clamp-3">
                          {story?.seoDescription}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center bg-neutral-50 p-6">
                    <div class="flex w-full justify-between">
                      <p class="text-sm font-medium text-neutral-900">
                        <span class="hover:underline">
                          {story?.firstByline}
                        </span>
                      </p>
                      {story?.timeToRead && (
                        <div class="flex space-x-1 text-sm text-neutral-500">
                          <span>{story?.timeToRead} min read </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`group flex -translate-y-1 transform flex-col overflow-hidden rounded-none rounded-3xl border-none opacity-100 shadow-sm shadow-2xl outline-none transition-all duration-500 visited:opacity-50 focus:-translate-y-1 focus:rounded-none focus:shadow-2xl motion-reduce:transition-none`}
                style={{
                  filter: 'blur(0px)',
                }}
              >
                <div class="relative flex-shrink-0 overflow-hidden bg-black">
                  {story?.mainImage ? (
                    <img
                      class="h-72 w-full -translate-y-12 scale-105 transform object-cover opacity-100 grayscale-0 filter transition-all duration-500 group-focus:-translate-y-12 group-focus:scale-105 group-focus:grayscale-0 md:h-96"
                      src={
                        story?.cardImage
                          ? urlFor(story?.cardImage)?.width(800).url()
                          : urlFor(story?.mainImage)?.width(800).url()
                      }
                      alt={story?.mainImage?.alt}
                    />
                  ) : (
                    <div class="h-72 w-full -translate-y-12 scale-105 transform object-cover opacity-100 grayscale-0 filter transition-all duration-500 group-focus:-translate-y-12 group-focus:scale-105 group-focus:grayscale-0 md:h-96"></div>
                  )}
                  <div class="absolute left-0 right-0 bottom-0 h-1/2 h-full bg-gradient-to-t from-black to-transparent opacity-70 opacity-100 transition-all duration-500"></div>
                </div>
                <div class="pointer-events-none absolute bottom-0 w-full translate-y-40 translate-y-0 transform transition-all duration-500 group-focus:translate-y-0">
                  <div class="flex flex-1 flex-col justify-between bg-transparent p-6">
                    <div class="flex-1">
                      <p class="text-sm font-medium text-white">
                        <span class="">Make a Difference.</span>
                      </p>
                      <div class="mt-2 block">
                        <p class="text-xl font-semibold text-white">
                          {story?.title}
                        </p>
                        <p class="mt-3 text-base text-neutral-100 line-clamp-3">
                          {story?.seoDescription}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center bg-neutral-50 p-6">
                    <div class="flex w-full justify-between">
                      <p class="text-sm font-medium text-neutral-900">
                        <span class="hover:underline">
                          {story?.firstByline}
                        </span>
                      </p>
                      {story?.timeToRead && (
                        <div class="flex space-x-1 text-sm text-neutral-500">
                          <span>{story?.timeToRead} min read </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <AnimatePresence>
          {showNotification === true && (
            <motion.div
              aria-live="assertive"
              class="pointer-events-none fixed inset-0 z-[99] flex items-end px-4 py-6 ease-in-out sm:items-end sm:p-6"
              initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div class="flex w-full flex-col items-center space-y-4 sm:items-center">
                <div class="pointer-events-auto w-full max-w-sm overflow-hidden bg-sv-navy shadow-lg ring-1 ring-black/5">
                  <div class="p-4">
                    <div class="flex items-start">
                      <div class="shrink-0">
                        <svg
                          class="h-6 w-6 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          aria-hidden="true"
                          data-slot="icon"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                      </div>
                      <div class="ml-3 w-0 flex-1 pt-0.5">
                        <p class="text-sm font-medium text-white">
                          Successfully copied link!
                        </p>
                        <p class="mt-1 text-sm text-white/70">
                          Paste the link to share with others.
                        </p>
                      </div>
                      <div class="ml-4 flex shrink-0">
                        <button
                          type="button"
                          class="inline-flex text-white/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-sv-light-blue focus:ring-offset-2 focus:ring-offset-sv-navy"
                          onClick={() => setShowNotification(false)}
                        >
                          <span class="sr-only">Close</span>
                          <svg
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          <div className="lg:left-112 xl:left-120 fixed inset-x-0 bottom-0 z-[100]">
            <AudioPlayer />
          </div>
        </AnimatePresence>
      </AudioProvider>
    </>
  )
}
